<template>
  <form class="visit-inspection-form-wrapper" @submit.prevent="onSubmit">
    <!-- Navigation sidebar -->
    <VisitInspectionSidebar
      :formSubmitting="formSubmitting"
      :savingForm="savingForm"
      :formSubmitted="formSubmitted"
      :saveAllowed="saveAllowed"
      :lastSavedDate="lastSavedDate"
      :showPreviousButton="!isEmailLink"
      :showAnnualQuestions="showAnnualQuestions"
      :hideAnnualQuestionsAllowed="hideAnnualQuestionsAllowed"
      :sectionsWithErrors="sectionsWithErrors"
      :plantId="otherCompaniesData[0]?.PlantId"
      :showDisclaimer="isCustomQuestionsForm"
      :isHistoricalForm="!isInitialFillingOutOfForm"
      @saveClicked="onSaveClicked"
      @update:showAnnualQuestions="showAnnualQuestions = $event"
      @applyNAToAll="onApplyNAToAll"
      @clearAllAnswers="onClearAllAnswers"
    />

    <div class="visit-inspection-form">
      <!-- Show results modal if initial form has been submitted -->
      <InspectionResultsModal
        v-if="formSubmitted && isInitialFillingOutOfForm"
        :visitId="Number(visitId)"
        :efrAvailable="efrAvailable"
        :failedAttachments="submissionResult?.FailedAttachmentFileNames ?? []"
        @closeModal="closeResultsModal"
      />
      <VisitConfirmationModal
        v-if="showConfirmationModal"
        :formData="formData"
        :otherCompaniesData="otherCompaniesData"
        :visitId="inspectionStore.hideVisitId ? undefined : visitId"
        :hasFilledHiddenAnnualQuestions="hasFilledHiddenAnnualQuestions"
        @closeModal="showConfirmationModal = false"
        @confirm="confirmSubmission"
      />

      <!--The following div container displays the page header info; previous button, labels for group visit, visit ID/type/Status and varioius messages-->
      <div class="visit-inspection-form__header-container">
        <h1 class="visit-inspection-form__header">
          <div class="visit-inspection-form__header-title">
            {{ inspectionStore.isGroupVisit && inspectionStore.visitType == "Regular" ? "Group " : "" }}Visit Inspection
            Form
            <BadgeIndicator v-if="inspectionStore.visitStatus" type="default" icon="tasks">
              {{ inspectionStore.isUrgentStatus ? "Urgent-" : "" }}{{ inspectionStore.visitStatus }}
            </BadgeIndicator>
          </div>
          <div class="visit-inspection-form__header-subtitle-row">
            <small class="visit-inspection-form__header-subtitle">
              {{ inspectionStore.visitType ? inspectionStore.visitType + " Visit" : "" }}
            </small>
            <Button
              v-if="showInitialVisitTemplateDownload"
              type="button"
              class="visit-inspection-form__sample-template-button"
              @click="downloadInitialVisitTemplate"
            >
              <font-awesome-icon icon="fa-solid fa-download" />
              <span> Sample Template for Initial Visits</span>
            </Button>
          </div>
        </h1>
        <div class="visit-inspection-form__header-plant-info">
          <div class="visit-inspection-form__header-plant-name">
            <a
              v-if="otherCompaniesData[0]?.PlantId"
              :href="`/oudirect/PlantHistory/${otherCompaniesData[0].PlantId}`"
              class="plant-name-link"
              >{{ formData.plantName }}</a
            >
            <div v-else>{{ formData.plantName }}</div>
            <div v-if="otherCompaniesData[0]?.PlantType">
              <span class="plant-type">[{{ otherCompaniesData[0]?.PlantType }}]</span>
            </div>
          </div>
          <div class="metadata">RFR Name: {{ formData.rfrName }}</div>
          <div
            v-if="isCustomQuestionsForm && plantCategories.length > 0"
            class="visit-inspection-form__header-plant-category"
          >
            <span class="metadata">Plant Categories: {{ plantCategories.join(" | ") }}</span>
          </div>
          <div class="metadata">{{ otherCompaniesData[0]?.FormattedAddress }}</div>
        </div>
        <div v-if="visitReport?.DateEnteredRFR" class="metadata">
          Submission Date: {{ new Date(visitReport?.DateEnteredRFR).toLocaleString() }}
        </div>
      </div>

      <div class="visit-inspection-form__content">
        <p v-if="showValidationSummary" :id="validationSummaryId" class="error-message">
          The form cannot be submitted. Please complete each of the fields that are marked with a red asterisk and
          verify that all attachments have valid extensions.
        </p>
        <div v-if="errorMessages.length > 0" class="error-message-wrapper">
          <p v-for="(error, i) in errorMessages" :key="i" class="error-message">
            <span
              v-if="error.questionId"
              class="error-message__link"
              @click="navigateToQuestion(error.questionId)"
              :title="'Click to navigate to this question'"
            >
              {{ error.message }}
            </span>
            <span v-else>{{ error.message }}</span>
          </p>
        </div>
        <p v-if="!efrAvailable && isInitialFillingOutOfForm">
          This Visit ID was used to process your payment for visit date
          {{ new Date(otherCompaniesData[0]?.DateVisited).toLocaleDateString() }}. Please continue to fill out the EIR
          form. (EFR form will not be allowed for this Visit ID.)"
        </p>
        <p v-if="showViewOnlyMessage">
          This form is for REVIEW ONLY – Contact the Main RC/RC for questions or comments.
        </p>

        <!--The following div container displays the known info for a visit. The first 4 rows are common to all visits. The last row is common to all visits. -->
        <!--This container can also be used for data input for the initial input for scheduled visits only. In such a case, the date label is not visible, the date textbox is visible -->
        <VisitInspectionSection
          :sectionNumber="1"
          :showNAOption="false"
          :defaultExpanded="false"
          title="Visit Info"
          id="company-info"
        >
          <div>
            <label>
              <b>Company Name{{ otherCompaniesData.length > 1 ? "s" : "" }}:</b>
            </label>
            <ul style="list-style: none; padding-left: 0; margin: 0">
              <li
                v-for="company in otherCompaniesData"
                :key="company.CompanyId"
                style="display: flex; align-items: center; gap: 6px"
              >
                {{ company.Name }} <span class="plant-status">[{{ company.PlantStatus }}]</span>
                <BadgeIndicator
                  v-if="company.GroupStatus === 'Main' && otherCompaniesData.length > 1"
                  text="Main"
                  type="info"
                />
              </li>
            </ul>
          </div>
          <div class="visit-inspection-form__visit-row">
            <p class="visit-inspection-form__visit-cell"><b>RC Name:</b> {{ formData.rcName }}</p>
            <p v-if="!inspectionStore.hideVisitId" class="visit-inspection-form__visit-cell">
              <b>Visit ID:</b> {{ props.visitId }}
            </p>
          </div>
          <div class="visit-inspection-form__visit-row">
            <p class="visit-inspection-form__visit-cell"><b>Plant Contact:</b> {{ formData.plantContact }}</p>
            <p class="visit-inspection-form__visit-cell"><b>Group Leader:</b> {{ formData.groupLeader }}</p>
          </div>

          <!-- Show only for not initial special visits -->
          <div
            v-if="
              !isInitialFillingOutOfForm &&
              otherCompaniesData.length > 0 &&
              otherCompaniesData[0].VisitType != 'Regular'
            "
            class="visit-inspection-form__visit-row"
          >
            <p class="visit-inspection-form__visit-cell"><b>Plant Address:</b> {{ formData.plantAddress }}</p>
            <p class="visit-inspection-form__visit-cell"><b>Plant State:</b> {{ formData.plantState }}</p>
          </div>
          <div
            v-if="
              !isInitialFillingOutOfForm &&
              otherCompaniesData.length > 0 &&
              otherCompaniesData[0].VisitType != 'Regular'
            "
            class="visit-inspection-form__visit-row"
          >
            <p class="visit-inspection-form__visit-cell"><b>Plant City:</b> {{ formData.plantCity }}</p>
            <p class="visit-inspection-form__visit-cell"><b>Plant Country:</b> {{ formData.plantCountry }}</p>
          </div>

          <!-- Show only for not initial special visits for assigned RFR -->
          <div
            v-if="
              !isInitialFillingOutOfForm &&
              otherCompaniesData.length > 0 &&
              otherCompaniesData[0].VisitType != 'Regular' &&
              isAssignedRfr
            "
            class="visit-inspection-form__visit-row"
          >
            <p class="visit-inspection-form__visit-cell"><b>Sent By RC Name:</b> {{ formData.sentByRcName }}</p>
            <p class="visit-inspection-form__visit-cell"><b>Sent By RC Email:</b> {{ formData.sentByRcEmail }}</p>
          </div>

          <div class="visit-inspection-form__visit-row">
            <p class="visit-inspection-form__visit-cell">
              <b>Plant Contact Email:</b> {{ formData.plantContactEmail }}
            </p>
            <p class="visit-inspection-form__visit-cell">
              <b>Plant Contact Phone:</b> {{ formData.plantContactPhone }}
            </p>
          </div>

          <div v-if="showSaveCommentsInstruction" class="visit-inspection-form__comment-instruction">
            <p>Updates to comments and/or visit date will be saved upon submit.</p>
          </div>
          <p class="visit-inspection-form__text">{{ submitComments }}</p>
        </VisitInspectionSection>

        <VisitInspectionSection
          :sectionNumber="2"
          v-model="generalCustomQuestions"
          :additionalPreviousResponses="generalCustomPreviousResponses"
          :showAnnualQuestions="showAnnualQuestions"
          :isAnswersModificationAllowed="isAnswersModificationAllowed"
          :showNAOption="isCustomQuestionsForm"
          title="General Questions"
        >
          <div v-if="isGroupVisit" class="visit-inspection-form__tasks">
            <p class="multiple-companies-label">
              Data entered applies to all companies unless otherwise specified.
            </p>
          </div>

          <div v-if="showJewishOwned" class="visit-inspection-form__question">
            <QuestionHeader
              questionText="Is this plant Jewish-Owned?"
              :invalid="fieldIsInvalid('jewishOwned')"
              :isAnnual="true"
            />
            <div class="visit-inspection-form__radio-wrapper">
              <RadioButton
                name="jewish-owned-rb"
                :value="true"
                :checked="formData.jewishOwned == true"
                @change="(e: RadioButtonChangeEvent) => (formData.jewishOwned = e.value)"
                label="Yes"
                :disabled="!isInitialFillingOutOfForm"
                data-cy="jewish-owned-rb"
              />
              <RadioButton
                name="jewish-owned-rb"
                :value="false"
                :checked="formData.jewishOwned == false"
                @change="(e: RadioButtonChangeEvent) => (formData.jewishOwned = e.value)"
                label="No"
                :disabled="!isInitialFillingOutOfForm"
                data-cy="jewish-owned-rb"
              />
            </div>
          </div>

          <div class="visit-inspection-form__question">
            <QuestionHeader questionText="Virtual Visit" />
            <div class="visit-inspection-form__radio-wrapper">
              <RadioButton
                name="virtual-visit-rb"
                :value="true"
                :checked="formData.virtualVisit == true"
                @change="(e: RadioButtonChangeEvent) => (formData.virtualVisit = e.value)"
                label="Yes"
                :disabled="!isInitialFillingOutOfForm"
                data-cy="virtual-visit-rb"
              />
              <RadioButton
                name="virtual-visit-rb"
                :value="false"
                :checked="formData.virtualVisit == false"
                @change="(e: RadioButtonChangeEvent) => (formData.virtualVisit = e.value)"
                label="No"
                :disabled="!isInitialFillingOutOfForm"
                data-cy="virtual-visit-rb"
              />
            </div>
          </div>

          <div id="visit-date" class="visit-inspection-form__visit-row">
            <div class="visit-inspection-form__visit-control mb-0">
              <label for="visit-date">*Visit Date (Range: {{ formData.visitDateRange }}): </label>
              <DatePicker
                class="visit-date-picker"
                id="visit-date"
                name="visit-date"
                :max="new Date()"
                v-model="formData.visitDate"
                :disabled="(!isDateModificationAllowed && !isInitialFillingOutOfForm) || disableVisitDate"
                @change="datePickerLoseFocus($event)"
                required
              />
            </div>
          </div>

          <div v-if="showTimeInPlant" class="">
            <div class="visit-inspection-form__visit-control">
              <label for="time-in-hours">Average Time Spent in Plant </label>
              <TimeSpanInput
                v-model="formData.timeSpent"
                :disabled="!isAnswersModificationAllowed"
                :required="!hideAnnualQuestionsAllowed"
              />
              <BadgeIndicator text="Annual" type="info" class="annual-badge" />
            </div>
          </div>

          <div id="plant-guide" class="visit-inspection-form__question">
            <QuestionHeader questionText="Plant Guide" :invalid="fieldIsInvalid('plantGuide')" />
            <div class="visit-inspection-form__visit-control">
              <DropDownList
                id="plant-guide"
                name="plant-guide"
                class="plant-guide-dropdown"
                :dataItems="plantGuides"
                v-model="formData.plantGuide"
                text-field="FormattedName"
                :disabled="!isAnswersModificationAllowed"
                required
              />
            </div>

            <div
              v-if="newPlantGuideSelected && isAnswersModificationAllowed"
              class="visit-inspection-form__plant-guide-new"
            >
              <div class="visit-inspection-form__visit-row">
                <div class="visit-inspection-form__visit-control column grow">
                  <label for="first-name">First Name: </label>
                  <input id="first-name" name="first-name" type="text" v-model="formData.guideFirstName" required />
                </div>
                <div class="visit-inspection-form__visit-control column grow">
                  <label for="last-name">Last Name: </label>
                  <input id="last-name" name="last-name" type="text" v-model="formData.guideLastName" required />
                </div>
                <div class="visit-inspection-form__visit-control column grow">
                  <label for="email">Email: </label>
                  <input id="email" name="email" type="text" v-model="formData.guideEmail" required />
                </div>
                <div class="visit-inspection-form__visit-control column grow">
                  <label for="phone">Phone: </label>
                  <input id="phone" name="phone" type="text" v-model="formData.guidePhone" required />
                </div>
              </div>
            </div>
          </div>

          <div v-if="showTeamList" id="team-list">
            <h2 for="team-list" class="visit-header">
              A DIFFERENT or SUBSTITUTE Team/Vaad member performed this visit
            </h2>
            <div class="visit-inspection-form__visit-control">
              <DropDownList
                id="team-list"
                name="team-list"
                class="team-list-dropdown"
                :dataItems="teamMembers"
                v-model="formData.teamMember"
                text-field="RfrName"
                data-item-key="RfrPersonJobId"
              />
            </div>
          </div>

          <div v-if="showEmailCopySection" id="email-copy" class="visit-inspection-form__question">
            <QuestionHeader questionText="Email a copy of this EIR report?" />
            <Checkbox
              id="email-copy-checkbox"
              class="visit-inspection-form__question-checkbox"
              v-model="formData.sendEmailCopy"
              label="Yes"
              data-cy="email-copy-checkbox"
              :disabled="!isAnswersModificationAllowed"
            />
            <div v-if="formData.sendEmailCopy">
              <p v-if="formData.plantContactEmail">
                Email will be sent to plant contact: {{ formData.plantContactEmail }}
              </p>
              <p for="email-recipients">
                <span class="invalid" v-if="fieldIsInvalid('emailRecipients')">*</span>
                Enter email recipient(s) separated by semicolons - NO SPACES:
              </p>
              <input
                class="visit-inspection-form__email-recipients"
                id="email-recipients"
                name="email-recipients"
                type="text"
                v-model="formData.emailRecipients"
                :disabled="!formData.sendEmailCopy"
              />
              <p style="font-weight: bold">
                *** Note: These email recipient(s) are NOT saved. Contact RC to configure any Company/Plant contact to
                receive all future EIR reports.
              </p>
            </div>
          </div>

          <div class="visit-inspection-form__question">
            <QuestionHeader
              questionText="General Comments, Issues and Corrections (e.g. visit issue / plant contact phone update)"
            />
            <InspectionCommentsView
              v-if="formData.savedGeneralComments?.length"
              :comments="formData.savedGeneralComments"
            />
            <textarea :rows="1" :cols="20" :maxlength="5000" v-model="formData.generalComments"></textarea>
          </div>

          <div class="visit-inspection-form__question">
            <QuestionHeader
              questionText="Confidential Comments (NOT visible to Company)"
              :invalid="fieldIsInvalid('confidentialComment')"
            />
            <InspectionCommentsView
              v-if="formData.savedConfidentialComment?.length"
              :comments="formData.savedConfidentialComment"
            />
            <textarea :rows="1" :cols="20" :maxlength="5000" v-model="formData.confidentialComment"></textarea>
          </div>

          <div v-if="showNonSpecialSection" class="visit-inspection-form__non-special">
            <div class="visit-inspection-form__question">
              <QuestionHeader
                questionText="The following urgent matter(s) were addressed during the visit. (detail below)"
              />
              <InspectionCommentsView
                v-if="formData.savedUrgentMatters?.length"
                :comments="formData.savedUrgentMatters"
              />
              <textarea :rows="1" :cols="20" :maxlength="5000" v-model="formData.urgentMatters"></textarea>
            </div>

            <div id="is-urgent" class="visit-inspection-form__question">
              <QuestionHeader
                questionText="Are there any URGENT matters that require attention/action? (detail below)"
                :invalid="fieldIsInvalid('isUrgentComment')"
              />
              <div class="visit-inspection-form__radio-wrapper">
                <RadioButton
                  :name="`urgent-rb`"
                  :value="true"
                  :checked="formData.isUrgent == true"
                  @change="(e: RadioButtonChangeEvent) => (formData.isUrgent = e.value)"
                  label="Yes"
                  :disabled="!isAnswersModificationAllowed"
                  data-cy="urgent-rb"
                />
                <RadioButton
                  :name="`urgent-rb`"
                  :value="false"
                  :checked="formData.isUrgent == false"
                  @change="(e: RadioButtonChangeEvent) => (formData.isUrgent = e.value)"
                  label="No"
                  :disabled="!isAnswersModificationAllowed"
                  data-cy="urgent-rb"
                />
              </div>
              <InspectionCommentsView
                v-if="formData.savedIsUrgentComment?.length"
                :comments="formData.savedIsUrgentComment"
              />
              <textarea :rows="1" :cols="20" :maxlength="5000" v-model="formData.isUrgentComment"></textarea>
            </div>
          </div>
        </VisitInspectionSection>

        <VisitInspectionSection
          v-if="showNonSpecialSection"
          :sectionNumber="3"
          title="Ingredients"
          :startQuestionNumber="2"
          v-model="ingredientsCustomQuestions"
          :showAnnualQuestions="showAnnualQuestions"
          :isAnswersModificationAllowed="isAnswersModificationAllowed"
          :showNAOption="isCustomQuestionsForm"
        >
          <div id="incorrect-ingredients" class="visit-inspection-form__question">
            <QuestionHeader
              questionText="Did you find missing or incorrect ingredients on the Schedule A?"
              :questionNumber="1"
            />
            <div class="visit-inspection-form__radio-wrapper">
              <RadioButton
                :name="`incorrect-ingredients-rb`"
                :value="true"
                :checked="formData.incorrectIngredientsFound == true"
                @change="(e: RadioButtonChangeEvent) => (formData.incorrectIngredientsFound = e.value)"
                label="Yes"
                :disabled="!isAnswersModificationAllowed"
                data-cy="incorrect-ingredients-rb"
              />
              <RadioButton
                :name="`incorrect-ingredients-rb`"
                :value="false"
                :checked="formData.incorrectIngredientsFound == false"
                @change="(e: RadioButtonChangeEvent) => (formData.incorrectIngredientsFound = e.value)"
                label="No"
                :disabled="!isAnswersModificationAllowed"
                data-cy="incorrect-ingredients-rb"
              />
            </div>

            <InspectionCommentsView
              v-if="formData.incorrectIngredientSavedComments?.length"
              :comments="formData.incorrectIngredientSavedComments"
            />
            <ButtonGroup v-show="formData.incorrectIngredientsFound">
              <Button
                :togglable="true"
                type="button"
                :selected="!scheduleACardsViewMode"
                @click="scheduleACardsViewMode = false"
                >Classic View</Button
              >
              <Button
                :togglable="true"
                type="button"
                :selected="scheduleACardsViewMode"
                @click="scheduleACardsViewMode = true"
                >Cards View</Button
              >
            </ButtonGroup>
            <IncorrectItemCardsView
              v-if="formData.incorrectIngredientsFound && scheduleACardsViewMode"
              :type="VisitIncorrectItemType.Ingredient"
              v-model="formData.incorrectIngredients"
              :disableModification="!isAnswersModificationAllowed"
            />
            <IncorrectItemsView
              v-if="formData.incorrectIngredientsFound && !scheduleACardsViewMode"
              :type="VisitIncorrectItemType.Ingredient"
              v-model="formData.incorrectIngredients"
              :disableModification="!isAnswersModificationAllowed"
            />
            <textarea :rows="1" :cols="20" :maxlength="5000" v-model="formData.incorrectIngredientComments"></textarea>
          </div>
        </VisitInspectionSection>

        <VisitInspectionSection
          v-if="showNonSpecialSection"
          :sectionNumber="4"
          title="Products"
          :startQuestionNumber="2"
          v-model="productsCustomQuestions"
          :showAnnualQuestions="showAnnualQuestions"
          :isAnswersModificationAllowed="isAnswersModificationAllowed"
          :showNAOption="isCustomQuestionsForm"
        >
          <div id="incorrect-products" class="visit-inspection-form__question">
            <QuestionHeader
              questionText="Did you find missing or incorrect products and/or labels on the Schedule B?"
              :questionNumber="1"
            />
            <div class="visit-inspection-form__radio-wrapper">
              <RadioButton
                :name="`incorrect-products-rb`"
                :value="true"
                :checked="formData.incorrectProductsFound == true"
                @change="(e: RadioButtonChangeEvent) => (formData.incorrectProductsFound = e.value)"
                label="Yes"
                :disabled="!isAnswersModificationAllowed"
                data-cy="incorrect-products-rb"
              />
              <RadioButton
                :name="`incorrect-products-rb`"
                :value="false"
                :checked="formData.incorrectProductsFound == false"
                @change="(e: RadioButtonChangeEvent) => (formData.incorrectProductsFound = e.value)"
                label="No"
                :disabled="!isAnswersModificationAllowed"
                data-cy="incorrect-products-rb"
              />
            </div>
            <InspectionCommentsView
              v-if="formData.incorrectProductSavedComments?.length"
              :comments="formData.incorrectProductSavedComments"
            />
            <ButtonGroup v-show="formData.incorrectProductsFound">
              <Button
                :togglable="true"
                type="button"
                :selected="!scheduleBCardsViewMode"
                @click="scheduleBCardsViewMode = false"
                >Classic View</Button
              >
              <Button
                :togglable="true"
                type="button"
                :selected="scheduleBCardsViewMode"
                @click="scheduleBCardsViewMode = true"
                >Cards View</Button
              >
            </ButtonGroup>
            <IncorrectItemCardsView
              v-if="formData.incorrectProductsFound && scheduleBCardsViewMode"
              :type="VisitIncorrectItemType.Product"
              v-model="formData.incorrectProducts"
              :disableModification="!isAnswersModificationAllowed"
            />
            <IncorrectItemsView
              v-if="formData.incorrectProductsFound && !scheduleBCardsViewMode"
              :type="VisitIncorrectItemType.Product"
              v-model="formData.incorrectProducts"
              :disableModification="!isAnswersModificationAllowed"
            />
            <textarea :rows="1" :cols="20" :maxlength="5000" v-model="formData.incorrectProductComments"></textarea>
          </div>
        </VisitInspectionSection>

        <VisitInspectionSection
          v-for="(section, index) in sortedSections"
          :key="index"
          :sectionNumber="4 + index + 1"
          :title="section.sectionName"
          v-model="section.answers"
          :showAnnualQuestions="showAnnualQuestions"
          :isAnswersModificationAllowed="isAnswersModificationAllowed"
        />

        <VisitInspectionSection
          v-if="showSpecialSection"
          :showNAOption="false"
          :sectionNumber="calculateEndSectionNumber(-1)"
          title="Special Tasks"
        >
          <Checkbox
            id="special-tasks-checkbox"
            class="visit-inspection-form__question-checkbox"
            v-model="formData.hideSpecialTasks"
            label="N/A - Apply N/A to all questions in this section"
            data-cy="special-tasks-checkbox"
            :disabled="!isAnswersModificationAllowed"
          />
          <p v-if="!formData.hideSpecialTasks">
            If selecting option <b>NO</b> for any special task (questions 5-12), please elaborate in the comment.
          </p>

          <TaskQuestion
            v-for="(questionItem, i) in formData.specialTaskQuestions"
            :key="i"
            :questionItem="questionItem"
            :invalid="fieldIsInvalid(questionItem.propertyName)"
            :disableSelection="!isAnswersModificationAllowed"
          />
        </VisitInspectionSection>

        <VisitInspectionSection :sectionNumber="calculateEndSectionNumber(0)" title="Documents" :showNAOption="false">
          <div id="uploaded-docs" class="visit-inspection-form__question">
            <QuestionHeader
              questionText="Upload Documents (No Financial Docs)"
              :invalid="fieldIsInvalid('uploadedDocs')"
            />
            <FormAttachmentsView :key="uploadKey" v-if="Number(visitId)" :visitId="Number(visitId)" />

            <FileUpload v-model="formData.uploadedDocs" />
          </div>
        </VisitInspectionSection>

        <VisitInspectionSection
          v-if="showReopenSection"
          title="Reopen Visit"
          :sectionNumber="calculateEndSectionNumber(1)"
          :showNAOption="false"
        >
          <!-- Show instructions and Reopen button only when visit is closed - not when its reopened -->
          <p v-if="visitClosed">
            Click Reopen to save any comments and Reopen this visit. You must add a reopen comment. All recipients will
            be notified.
          </p>
          <InspectionCommentsView v-if="formData.reopenSavedComment?.length" :comments="formData.reopenSavedComment" />
          <textarea :rows="1" :cols="20" :maxlength="5000" v-model="formData.reopenComment"></textarea>
          <LoadingButton
            v-if="visitClosed"
            class="visit-inspection-form__reopen-button"
            type="button"
            themeColor="secondary"
            @click="onReopenClicked"
            :loading="formSubmitting || savingForm"
            :disabled="formSubmitted"
            >Reopen Visit</LoadingButton
          >
        </VisitInspectionSection>

        <VisitInspectionSection
          v-if="showCloseSection"
          title="Close Visit"
          :sectionNumber="calculateEndSectionNumber(1)"
          :showNAOption="false"
        >
          <p>*** MAIN RC: {{ formData.rcName }}</p>
          <p class="visit-inspection-form__text">
            Click Reviewed & Closed to save any comments and update the EIR status to Closed. All recipients will be
            notified.
          </p>
          <p v-if="requireUrgentComment" class="visit-inspection-form__text">
            For Urgent EIR's you MUST add a comment to Question 4 detailing the steps you have taken to resolve the
            URGENT matter(s).
          </p>
          <LoadingButton
            class="visit-inspection-form__close-button"
            type="button"
            themeColor="secondary"
            @click="onCloseClicked"
            :loading="formSubmitting || savingForm"
            :disabled="formSubmitted"
            >Reviewed & Closed
          </LoadingButton>
        </VisitInspectionSection>
      </div>
    </div>

    <!-- Floating submit button only visible on mobile -->
    <Button
      class="visit-inspection-form__mobile-submit"
      type="submit"
      :disabled="formSubmitting || savingForm || formSubmitted"
    >
      <font-awesome-icon icon="fa-solid fa-check" />
    </Button>
  </form>
</template>

<script lang="ts">
export interface NavigationItem {
  id: string;
  label: string;
  visible: boolean;
  children?: NavigationItem[];
  callback?: () => void;
}

interface AnswerDisplayBySection {
  sectionName: string;
  answers: VisitInspectionAnswerDisplay[];
  sectionOrder: number;
}
</script>

<script setup lang="ts">
import { computed, onBeforeMount, onUnmounted, ref, watch, watchEffect } from "vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { Checkbox, type RadioButtonChangeEvent } from "@progress/kendo-vue-inputs";
import RadioButton from "@/shared/ui/inputs/RadioButton.vue";
import TimeSpanInput from "@/shared/ui/inputs/TimeSpanInput.vue";
import { DatePicker, type DatePickerChangeEvent } from "@progress/kendo-vue-dateinputs";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import IncorrectItemsView from "./IncorrectItemsView.vue";
import IncorrectItemCardsView from "./IncorrectItemCardsView.vue";
import { VisitIncorrectItemType } from "@/Domain/VisitIncorrectItemType";
import TaskQuestion from "./TaskQuestion.vue";
import { ButtonGroup, Button } from "@progress/kendo-vue-buttons";
import InspectionCommentsView from "./InspectionCommentsView.vue";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type { InspectionVisitOtherCompany } from "@/Domain/InspectionVisitOtherCompany";
import { useInspectionStore, type FormData } from "@/stores/useInspectionStore";
import type { CartDisplayReport } from "@/Domain/CartDisplayReport";
import { UserRoles } from "@/Domain/UserRoles";
import type { EirFormComment } from "@/Domain/EirFormComment";
import type { InspectionFacilityContact } from "@/Domain/InspectionFacilityContact";
import type { SubmitRfrVisitInspection } from "@/Models/Requests/SubmitRfrVisitInspection";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { getAttachmentFilesAsync } from "@/shared/helpers/fileHelper";
import FormAttachmentsView from "./FormAttachmentsView.vue";
import moment from "moment";
import type { EirFormData } from "@/Models/EirFormData";
import type { UploadVisitInspectionFilesRequest } from "@/Models/Requests/UploadVisitInspectionFilesRequest";
import type { ModifyRfrVisitInspection } from "@/Models/Requests/ModifyRfrVisitInspection";
import type { InspectionRfrTeamMember } from "@/Domain/InspectionRfrTeamMember";
import { NewVisitsService } from "@/shared/services/NewVisitsService";
import InspectionResultsModal from "./InspectionResultsModal.vue";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import { EnumVisitMode } from "@/Domain/EnumVisitMode";
import type { SharepointUploadAttachmentsResult } from "@/Models/Results/SharepointUploadAttachmentsResult";
import VisitConfirmationModal from "./VisitConfirmationModal.vue";
import FileUpload from "@/shared/ui/inputs/FileUpload.vue";
import { isMobile } from "@/shared/helpers/mobileHelper";
import { Pages } from "@/pages/pages";
import type { VisitInspectionAdditionalRequirementsDisplay } from "@/Domain/VisitInspectionAdditionalRequirementsDisplay";
import type { VisitInspectionAnswerDisplay } from "@/Domain/VisitInspectionAnswerDisplay";
import VisitInspectionSection from "./VisitInspectionSection.vue";
import VisitInspectionSidebar from "./VisitInspectionSidebar.vue";
import { datePickerLoseFocus } from "@/shared/helpers/datePickerHelper";
import { EnumVisitQuestionType } from "@/Domain/EnumVisitQuestionType";
import BadgeIndicator from "@/shared/ui/BadgeIndicator.vue";
import type { VisitQuestionDisplay } from "@/Domain/VisitQuestionDisplay";
import QuestionHeader from "./QuestionHeader.vue";
import { useQuestionVisibility } from "@/composables/inspections/useQuestionVisibility";

const main = useMainStore();
const notificationStore = useNotificationStore();
const inspectionStore = useInspectionStore();
const fieldIsInvalid = inspectionStore.fieldIsInvalid;
const { isQuestionVisible } = useQuestionVisibility();

const props = defineProps<{
  mode: "new" | "view";
  type: EnumVisitMode;
  visitId?: string;
  menuUserRole?: string;
  inlineMode?: boolean;
  isEmailLink?: boolean;
}>();

const emits = defineEmits<{
  (e: "submitted", formData: FormData): void;
  (e: "reopened", formData: FormData): void;
  (e: "reviewedClosed", formData: FormData): void;
}>();

const plantGuideDefault = {
  FormattedName: "",
} as unknown as InspectionFacilityContact;

const plantGuideNewContact = {
  FormattedName: "Add New Contact",
} as unknown as InspectionFacilityContact;

const teamMemberDefault = {
  RfrName: "",
  RfrPersonJobId: null,
  RfrKashLogin: "",
} as unknown as InspectionRfrTeamMember;

const showValidationSummary = ref<boolean>(false);
const validationSummaryId = "validation-summary";

interface ErrorMessage {
  message: string;
  questionId?: number;
}

const errorMessages = ref<ErrorMessage[]>([]);

const otherCompaniesData = ref<InspectionVisitOtherCompany[]>([]);
const visitReport = ref<CartDisplayReport>();
const disableVisitDate = ref(true);
const uploadKey = ref(0);
const savingForm = ref(false);
let savedFormState: EirFormData | null = null;
const lastSavedDate = ref<Date>();
const showConfirmationModal = ref(false);
const submissionConfirmed = ref(false);

const visitTypes = [
  { text: "Select...", value: null },
  { text: "Initial", value: EnumVisitType.Initial },
  { text: "Regular", value: EnumVisitType.Regular },
  { text: "Special Production", value: EnumVisitType.SpecialProduction },
  { text: "Passover", value: EnumVisitType.Passover },
  { text: "Review", value: EnumVisitType.Review },
];

const plantGuides = ref<InspectionFacilityContact[]>([]);

const teamMembers = ref<InspectionRfrTeamMember[]>([]);
const requirements = ref<VisitInspectionAdditionalRequirementsDisplay | null>(null);

const formSubmitting = ref(false);
const formSubmitted = ref(false);
const submissionResult = ref<SharepointUploadAttachmentsResult>();

const scheduleACardsViewMode = ref(isMobile());
const scheduleBCardsViewMode = ref(isMobile());

const generalCustomPreviousResponses = ref<VisitInspectionAnswerDisplay[]>([]);
const generalCustomQuestions = ref<VisitInspectionAnswerDisplay[]>([]);
const ingredientsCustomQuestions = ref<VisitInspectionAnswerDisplay[]>([]);
const productsCustomQuestions = ref<VisitInspectionAnswerDisplay[]>([]);
const sortedSections = ref<AnswerDisplayBySection[]>([]);
const showAnnualQuestions = ref(true);

const getDefaultFormData = function (): FormData {
  return {
    visitType: visitTypes[0],
    visitDate: undefined,
    timeSpent: "",
    requestingRc: null,
    selectedRc: null,
    plantName: "",
    companyName: "",
    rfrName: "",
    rcName: "",
    plant: null,
    plantAddress: "",
    groupLeader: "",
    visitDateRange: "",
    plantCity: "",
    plantState: "",
    plantCountry: "",
    plantContact: "",
    sentByRcName: "",
    sentByRcEmail: "",
    plantContactEmail: "",
    plantContactPhone: "",
    plantGuide: null,
    guideFirstName: "",
    guideLastName: "",
    guideEmail: "",
    guidePhone: "",
    teamMember: null,
    generalComments: "",
    savedGeneralComments: [],
    confidentialComment: "",
    savedConfidentialComment: [],
    incorrectIngredientsFound: false,
    incorrectProductsFound: false,
    incorrectIngredients: [],
    incorrectProducts: [],
    incorrectIngredientComments: "",
    incorrectIngredientSavedComments: [],
    incorrectProductComments: "",
    incorrectProductSavedComments: [],
    urgentMatters: "",
    savedUrgentMatters: [],
    isUrgent: false,
    isUrgentComment: "",
    savedIsUrgentComment: [],
    sendEmailCopy: false,
    emailRecipients: "",
    hideSpecialTasks: false,
    specialTaskQuestions: [],
    uploadedDocs: [],
    reopenComment: "",
    reopenSavedComment: [],
    virtualVisit: false,
    invalidFields: [],
    jewishOwned: null,
    answers: [],
  };
};

const formData = ref<FormData>(getDefaultFormData());
inspectionStore.formData = formData.value;

const specialQuestions = [
  { text: "5. Reviewed heat graph:", propertyName: "HeatGraphs" },
  {
    text: "6. Reviewed production records to verify proper sequence & line integrity:",
    propertyName: "IntegrityOfLines",
  },
  { text: "7. Reviewed CIP records:", propertyName: "CipRecords" },
  {
    text: "8. Reviewed bills of lading (for bulk deliveries):",
    propertyName: "BillsOfLading",
  },
  {
    text: "9. Verified kosher status of carrier:",
    propertyName: "CarrierStatus",
  },
  { text: "10. Traced lines:", propertyName: "TraceLines" },
  { text: "11. Reviewed batch sheets:", propertyName: "BatchSheets" },
  {
    text: "12. Reviewed master formula cards:",
    propertyName: "FormulaCards",
  },
];

const populateSpecialQuestions = function () {
  formData.value.specialTaskQuestions = specialQuestions.map((question) => {
    return {
      question: question.text,
      answer: undefined as boolean | null,
      haveIssues: false,
      comments: "",
      savedComments: [] as EirFormComment[],
      propertyName: question.propertyName,
    };
  });
};

const getSpecialTask = function (propertyName: string) {
  return formData.value.specialTaskQuestions.find((q) => q.propertyName == propertyName);
};

const getSpecialTaskAnswer = function (propertyName: string) {
  if (formData.value.hideSpecialTasks) return "NA";

  const task = getSpecialTask(propertyName);
  if (!task || task.answer === undefined) return "";
  if (task.answer == null) return "NA";
  if (task.answer && task.haveIssues) return "Iss";

  return task.answer ? "NoIss" : "No";
};

populateSpecialQuestions();

const isCustomQuestionsForm = computed(() => {
  return (
    ((requirements.value?.Questions &&
      requirements.value.Questions.filter((q) => q.Section.SectionName !== "General Questions" || q.IsDynamicQuestion)
        .length > 0) ||
      (visitReport.value && visitReport.value.QuestionsAndAnswers?.length > 0)) ??
    false
  );
});

const plantCategories = computed(() => {
  return otherCompaniesData.value
    .flatMap((company) => company.PlantCategories)
    .filter((value, index, self) => self.indexOf(value) === index);
});

const isAssignedRfr = computed(() => {
  return (
    otherCompaniesData.value[0]?.RfrLogin?.trim().toLowerCase() == main.userLogin.trim().toLowerCase() ||
    props.menuUserRole == "VisitsAsRfr"
  );
});

const isMainRc = computed(() => {
  return otherCompaniesData.value[0]?.RcLogin?.trim().toLowerCase() == main.userLogin.trim().toLowerCase();
});

const isDateModificationAllowed = computed(() => {
  return otherCompaniesData.value[0]?.DateModificationAllowed && isAssignedRfr;
});

const efrAvailable = computed(() => {
  return !(!otherCompaniesData.value[0] || new Date(otherCompaniesData.value[0]?.DateVisited).getFullYear() > 1);
});

const isAnswersModificationAllowed = computed(() => {
  return !visitReport.value;
});

const visitClosed = computed(() => {
  const report = visitReport.value;
  if (!report) return false;
  return new Date(report.DateClosedRC).getFullYear() > 1;
});

const showViewOnlyMessage = computed(() => {
  return !(isMainRc.value || isAssignedRfr.value) && !visitClosed.value;
});

const showTeamList = computed(() => {
  return (
    teamMembers.value.length > 0 &&
    teamMembers.value.some(
      (teamMember) =>
        main.userLogin.toLowerCase() == teamMember.RfrKashLogin.toLowerCase() &&
        teamMember.IsTeamLeader &&
        teamMember.EnableEirTeamVisitsForTeamLeader
    ) &&
    isInitialFillingOutOfForm.value
  );
});

const authorizedEirRole = computed(() => {
  return (
    isMainRc.value ||
    main.isInAnyRole([
      UserRoles.ROLE_RABBINIC_COORDINATOR,
      UserRoles.ROLE_IN_HOUSE_MIS_MANAGER,
      UserRoles.ROLE_RC_SUPPORT_STAFF,
    ])
  );
});

// 'The Main RC OR the RFR on the plant can submit comments
// for non specials and date changes for specials and regulars.
// (For Specials there is no comments allowed).
const showSaveCommentsInstruction = computed(() => {
  return (
    !isInitialFillingOutOfForm.value &&
    (isAssignedRfr.value || (authorizedEirRole.value && inspectionStore.visitType == "Regular"))
  );
});

const isRegularVisit = computed(() => {
  return formData.value.visitType.value == EnumVisitType.Regular;
});

const isSpecialVisit = computed(() => {
  return !isRegularVisit.value;
});

// Non Special section is shown for Regular visits and for Custom Questions forms
const showNonSpecialSection = computed(() => {
  return isRegularVisit.value || isCustomQuestionsForm.value;
});

// Special section is shown only for Regular visits that are not Custom Questions forms
const showSpecialSection = computed(() => {
  return inspectionStore.formLoaded && isRegularVisit.value && !isCustomQuestionsForm.value;
});

const isGroupVisit = computed(() => {
  return otherCompaniesData.value.length > 1;
});

const showEmailCopySection = computed(() => {
  return (
    showNonSpecialSection.value &&
    //!formData.value.isUrgent &&  // see Azure item #28440
    !isGroupVisit.value
  );
});

const isInitialFillingOutOfForm = computed(() => {
  return props.mode == "new" && !visitReport.value;
});

const showInitialVisitTemplateDownload = computed(() => {
  return inspectionStore.visitType == "Initial" || formData.value.visitType.value == EnumVisitType.Initial;
});

// Not isInitialFillingOutOfForm And (isAssignedRFR() Or authorizedEirRole)
// The Main RC OR the RFR on the plant can submit comments for all visits
const modificationAllowed = computed(() => {
  return !isInitialFillingOutOfForm.value && (isAssignedRfr.value || authorizedEirRole.value);
});

const saveAllowed = computed(() => {
  return isInitialFillingOutOfForm.value && isAssignedRfr.value;
});

const hasFilledHiddenAnnualQuestions = computed(() => {
  if (showAnnualQuestions.value) return false;
  return formData.value.answers.some((a) => a.IsAnnual && (a.AnswerValue || a.AnswerText));
});

// Allow hiding annual questions if all annual questions have previous responses
const hideAnnualQuestionsAllowed = computed(() => {
  return (
    (!isInitialFillingOutOfForm.value ||
      (isCustomQuestionsForm.value && !requirements.value?.DisplayAnnualQuestions)) ??
    false
  );
});

const showCloseSection = computed(() => {
  return !isInitialFillingOutOfForm.value && !visitClosed.value && authorizedEirRole.value;
});

const newPlantGuideSelected = computed(() => {
  return formData.value.plantGuide?.FormattedName == plantGuideNewContact.FormattedName;
});

const defaultPlantGuideSelected = computed(() => {
  return formData.value.plantGuide?.FormattedName == plantGuideDefault.FormattedName;
});

// Visit must be closed in order to reopen. We also show this section
// if visit is already reopened in order to see the running reopen comments
const showReopenSection = computed(() => {
  return (
    (!isInitialFillingOutOfForm.value && visitClosed.value) ||
    visitReport.value?.EIRStatus.toLowerCase().includes("reopen")
  );
});

const requireUrgentComment = computed(() => {
  return (
    visitReport.value &&
    visitReport.value.EIRStatus.includes("Urgent") &&
    !visitReport.value.EIRStatus.toLowerCase().includes("reopen")
  );
});

//35327 - don't show general annual questions for special visits (short form)
const showTimeInPlant = computed(() => {
  return (
    (showAnnualQuestions.value && isInitialFillingOutOfForm.value && !isSpecialVisit.value) ||
    requirements.value?.TimeInPlantQuestionRequired ||
    (visitReport.value && visitReport.value.TimeIn)
  );
});

//35327 - don't show general annual questions for special visits (short form)
const showJewishOwned = computed(() => {
  return (
    (showAnnualQuestions.value && isInitialFillingOutOfForm.value && !isSpecialVisit.value) ||
    requirements.value?.JewishOwnedQuestionRequired ||
    (visitReport.value && visitReport.value.JewishOwned != null)
  );
});

const submitComments = computed(() => {
  if (!modificationAllowed.value) return "";

  if (isAssignedRfr.value && isSpecialVisit.value) return "Notification will be sent to the RC.";

  // RFR looking at submitted form that is not closed
  if (isAssignedRfr.value && !visitClosed.value) return "Notification will be sent to the RC.";

  // RFR looking at closed form
  if (isAssignedRfr.value && visitClosed.value) return "Notification will NOT be sent to the RC. This visit is closed.";

  if (isMainRc.value && visitReport.value?.EIRStatus == "Submitted")
    return "Notification will be sent to the RFR. EIR status will be updated to Reviewed.";

  if (isMainRc.value && !visitClosed.value) return "Notification will be sent to the RFR.";

  // Main RC looking at his closed visit
  if (isMainRc.value && visitClosed.value) return "Notification will NOT be sent to the RFR. This visit is closed.";

  return "";
});

const emailRegex = () => /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;

// Get all custom question errors (errors with questionId)
const customQuestionErrors = computed(() => {
  return errorMessages.value.filter((error) => error.questionId !== undefined);
});

// Check if there are any custom question errors
const hasCustomQuestionErrors = computed(() => {
  return customQuestionErrors.value.length > 0;
});

// Get sections that have errors
const sectionsWithErrors = computed(() => {
  const sectionsSet = new Set<string>();

  customQuestionErrors.value.forEach((error) => {
    if (!error.questionId) return;

    const answer = formData.value.answers.find((a) => a.QuestionId === error.questionId);
    if (answer) {
      sectionsSet.add(answer.SectionName);
    }
  });

  return sectionsSet;
});

const navigateToQuestion = function (questionId: number) {
  // Find the answer in formData
  const answer = formData.value.answers.find((a) => a.QuestionId === questionId);
  if (!answer || !answer.SectionName) return;

  const sectionToScrollTo = answer.SectionName;

  // Find the section in the store and scroll to it
  const sectionEntry = Object.entries(inspectionStore.sections).find(
    ([, section]) => section.name === sectionToScrollTo
  );
  if (sectionEntry) {
    const [sectionId] = sectionEntry;
    // Expand the section without scrolling
    inspectionStore.expandSection(sectionId);

    // After section is visible, scroll to the specific question
    setTimeout(() => {
      const questionElement = document.getElementById(`question-${questionId}`);
      if (questionElement) {
        questionElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
  }
};

const validateInitialForm = function () {
  errorMessages.value = [];
  formData.value.invalidFields = [];
  showValidationSummary.value = false;

  // Validate the uploaded file types
  if (formData.value.uploadedDocs.some((d) => d.validationErrors?.includes("invalidFileExtension"))) {
    errorMessages.value.push({ message: "Invalid document type. Please upload files with valid extensions." });
  }

  // 21343: Short Form visit (Non-Regular visits on standard plants or Special Production on Bakery plants (for now) must have at least one attachment or a comment (min 3 characters)
  if (
    isSpecialVisit.value &&
    !isCustomQuestionsForm.value &&
    formData.value.visitType.value &&
    formData.value.uploadedDocs.length == 0 &&
    formData.value.confidentialComment.trim().length < 3 &&
    formData.value.generalComments.trim().length < 3
  ) {
    errorMessages.value.push({ message: "At least one attachment or a comment is required." });
    formData.value.invalidFields.push("confidentialComment");
    formData.value.invalidFields.push("generalComments");
    formData.value.invalidFields.push("uploadedDocs");
  }

  // Validate plant guide is selected
  if (defaultPlantGuideSelected.value) {
    errorMessages.value.push({ message: "Please select a plant guide." });
    formData.value.invalidFields.push("plantGuide");
  }

  // Validate emails
  if (formData.value.sendEmailCopy) {
    const emails = formData.value.emailRecipients?.split(";").filter((e) => e) ?? [];
    for (const email of emails) {
      if (!emailRegex().test(email)) {
        errorMessages.value.push({ message: "Invalid email address: " + email });
        formData.value.invalidFields.push("emailRecipients");
      }
    }
  }

  // Must have comments if question 4 is YES (URGENT)
  if (formData.value.isUrgent && !formData.value.isUrgentComment) {
    errorMessages.value.push({
      message: "Please explain the URGENT matters that require attention/action in the comments after Question 4.",
    });
    formData.value.invalidFields.push("isUrgentComment");
  }

  // Validate special tasks (only for regular visits and if special tasks are not hidden)
  if (showSpecialSection.value && !formData.value.hideSpecialTasks) {
    for (const question of formData.value.specialTaskQuestions) {
      if (question.answer === undefined) {
        errorMessages.value.push({
          message: `Please answer special task question ${question.question.replaceAll(":", "")}.`,
        });
        formData.value.invalidFields.push(question.propertyName);
      }
    }
  }

  // Validate custom questions
  for (const answer of formData.value.answers) {
    if (!answer.IsRequired) continue;

    // Skip questions that are not visible due to dependency rules
    if (!isQuestionVisible(answer)) continue;

    // Skip annual questions if they are hidden (toggle off) or were filled out previously
    if (answer.IsAnnual && (!showAnnualQuestions.value || (answer.PreviousResponses && answer.PreviousResponses.length > 0))) continue;
    if (
      (!answer.AnswerValue && answer.VisitQuestionType != EnumVisitQuestionType.Comment) ||
      (answer.VisitQuestionType == EnumVisitQuestionType.Comment && !answer.AnswerText)
    ) {
      errorMessages.value.push({
        message: `Please answer the question: ${answer.QuestionText}`,
        questionId: answer.QuestionId,
      });
      formData.value.invalidFields.push("question-" + answer.QuestionId);
    }
  }

  // Validate Jewish Owned question
  if (showJewishOwned.value && requirements.value?.JewishOwnedQuestionRequired && formData.value.jewishOwned == null) {
    errorMessages.value.push({ message: "Please answer the Jewish Owned question." });
    formData.value.invalidFields.push("jewishOwned");
  }

  // Show validation summary if there are errors
  if (errorMessages.value.length > 0) {
    showValidationSummary.value = true;

    setTimeout(() => {
      const formElement = document.getElementById(validationSummaryId);
      formElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);

    return false;
  }

  return true;
};

const createSubmitModelAsync = async function () {
  const files = await getAttachmentFilesAsync(formData.value.uploadedDocs);

  return {
    VisitId: Number(props.visitId) ?? 0,
    VisitDate: moment(formData.value.visitDate).format("MM/DD/yyyy"),
    TimeIn: formData.value.timeSpent || "",
    JewishOwned: formData.value.jewishOwned,
    SchedA: formData.value.incorrectIngredientsFound ? "Yes" : "No",
    SchedAComments: formData.value.incorrectIngredientComments,
    SchedB: formData.value.incorrectProductsFound ? "Yes" : "No",
    SchedBComments: formData.value.incorrectProductComments,
    UrgentRfrComments: formData.value.urgentMatters,
    UrgentActionReq: formData.value.isUrgent ? "Yes" : "No",
    UrgentComments: formData.value.isUrgentComment,
    HeatGraphs: getSpecialTaskAnswer("HeatGraphs"),
    HeatGraphsComment: getSpecialTask("HeatGraphs")?.comments,
    IntegrityOfLines: getSpecialTaskAnswer("IntegrityOfLines"),
    IntegrityOfLinesComment: getSpecialTask("IntegrityOfLines")?.comments,
    CipRecords: getSpecialTaskAnswer("CipRecords"),
    CipRecordsComment: getSpecialTask("CipRecords")?.comments,
    BillsOfLading: getSpecialTaskAnswer("BillsOfLading"),
    BillsOfLadingComment: getSpecialTask("BillsOfLading")?.comments,
    CarrierStatus: getSpecialTaskAnswer("CarrierStatus"),
    CarrierStatusComment: getSpecialTask("CarrierStatus")?.comments,
    TraceLines: getSpecialTaskAnswer("TraceLines"),
    TraceLinesComment: getSpecialTask("TraceLines")?.comments,
    BatchSheets: getSpecialTaskAnswer("BatchSheets"),
    BatchSheetsComment: getSpecialTask("BatchSheets")?.comments,
    FormulaCards: getSpecialTaskAnswer("FormulaCards"),
    FormulaCardsComment: getSpecialTask("FormulaCards")?.comments,
    GeneralComments: formData.value.generalComments,
    ConfidentialComment: formData.value.confidentialComment,
    NameOfPlantEscort: formData.value.plantGuide?.ContactId ? formData.value.plantGuide?.FormattedName : "",
    HtmlString: "",
    SchedAGrid: formData.value.incorrectIngredients,
    SchedBGrid: formData.value.incorrectProducts,
    SendToPlant: formData.value.sendEmailCopy ? "Yes" : "No",
    PlantEmail:
      (formData.value.sendEmailCopy ? formData.value.emailRecipients : "") + ";" + formData.value.plantContactEmail,
    ContactFirstName: newPlantGuideSelected.value ? formData.value.guideFirstName : "",
    ContactLastName: newPlantGuideSelected.value ? formData.value.guideLastName : "",
    ContactEmail: newPlantGuideSelected.value ? formData.value.guideEmail : "",
    ContactPhone: newPlantGuideSelected.value ? formData.value.guidePhone : "",
    OverrideRfrForTeamLeaderPjId: formData.value.teamMember?.RfrPersonJobId?.toString() ?? "",
    VirtualVisit: formData.value.virtualVisit ? "Y" : "N",
    Files: files,
    // Trim Answers to only include necessary fields for saving
    // Exclude annual answers when annual questions are hidden (toggle off)
    Answers: formData.value.answers
      .filter((a) => isQuestionVisible(a) && (showAnnualQuestions.value || !a.IsAnnual))
      .map((a) => ({
        QuestionId: a.QuestionId,
        AnswerText: a.AnswerText,
        AnswerValue: a.AnswerValue,
      })),
  } as SubmitRfrVisitInspection;
};

const createSaveModelAsync = async function () {
  const model = await createSubmitModelAsync();
  model.PlantEmail = formData.value.emailRecipients;
  return model;
};

const onSubmit = async function () {
  try {
    // Expand all sections to initiate proper validation
    inspectionStore.expandAllSections();
    // If form is in view mode, only submit new comments
    if (modificationAllowed.value) {
      return await addCommentsAsync(false, false, true);
    } else if (!isInitialFillingOutOfForm.value) {
      // User is not authorized to submit the form in view mode - show warning that submission is not allowed
      notificationStore.notify({
        message: "Submission is not allowed",
        type: NotificationTypeEnum.warning,
        duration: 5000,
      });
      return;
    }

    formSubmitting.value = true;

    if (!validateInitialForm()) return;

    if (!submissionConfirmed.value) {
      showConfirmationModal.value = true;
      return;
    }

    const submitModel = await createSubmitModelAsync();
    submitModel.SchedAGrid = submitModel.SchedAGrid.filter((i) => i.IsIncorrect == true || i.IsIncorrect == false);
    submitModel.SchedBGrid = submitModel.SchedBGrid.filter((p) => p.IsIncorrect == true || p.IsIncorrect == false);

    const showSubmitError = () => {
      notificationStore.notify({
        message: "Error submitting form. Please try again later.",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    };

    console.log("VisitInspectionForm, submitting", formData.value);

    await handleResponsePromiseAsync(
      api.post("SubmitRfrVisitInspection", submitModel),
      (data) => {
        if (!data || !data.Success) {
          showSubmitError();
          return;
        }
        submissionResult.value = data.Data;
        NewVisitsService.removeNewVisit(submitModel.VisitId);
        formSubmitted.value = true;
        notificationStore.notify({
          message: "Form submitted successfully",
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
      },
      "VisitInspectionForm.vue",
      "onSubmit",
      () => showSubmitError()
    );
  } finally {
    formSubmitting.value = false;
  }
};

const addCommentsAsync = async function (
  visitClosed: boolean = false,
  visitReopened: boolean = false,
  updateVisitDate: boolean = false
) {
  try {
    formSubmitting.value = true;

    const visitDate = moment(formData.value.visitDate).format("MM/DD/yyyy");
    const files = await getAttachmentFilesAsync(formData.value.uploadedDocs);

    const modifyModel = {
      VisitId: Number(props.visitId) ?? 0,
      VisitDate: visitDate,
      UpdateVisitDate: updateVisitDate,
      SchedAComment: formData.value.incorrectIngredientComments,
      SchedBComment: formData.value.incorrectProductComments,
      UrgentRfrComment: formData.value.urgentMatters,
      UrgentComment: formData.value.isUrgentComment,
      HeatGraphsComment: getSpecialTask("HeatGraphs")?.comments,
      IntegrityOfLinesComment: getSpecialTask("IntegrityOfLines")?.comments,
      CipRecordsComment: getSpecialTask("CipRecords")?.comments,
      BatchSheetsComment: getSpecialTask("BatchSheets")?.comments,
      FormulaCardsComment: getSpecialTask("FormulaCards")?.comments,
      BillsOfLadingComment: getSpecialTask("BillsOfLading")?.comments,
      CarrierStatusComment: getSpecialTask("CarrierStatus")?.comments,
      TraceLinesComment: getSpecialTask("TraceLines")?.comments,
      GeneralComment: formData.value.generalComments,
      RcClosed: visitClosed ? "Y" : "N",
      Reopened: visitReopened ? "Y" : "N",
      ReopenedComment: formData.value.reopenComment,
      ConfComment: formData.value.confidentialComment,
      Files: files,
      QuestionComments: formData.value.answers
        .filter((a) => a.AnswerText)
        .map((a) => ({
          QuestionId: a.QuestionId,
          AnswerText: a.AnswerText,
        })),
    } as ModifyRfrVisitInspection;

    await handleResponsePromiseAsync(
      api.post("ModifyRfrVisitInspection", modifyModel),
      () => {
        formSubmitted.value = true;
        notificationStore.notify({
          message: "Form submitted successfully",
          type: NotificationTypeEnum.success,
          duration: 5000,
        });

        // Inline mode - only emit the submitted event, no redirection
        if (props.inlineMode) {
          if (visitReopened) emits("reopened", formData.value);
          else if (visitClosed) emits("reviewedClosed", formData.value);
          else emits("submitted", formData.value);
          return;
        }

        window.location.href = `${Pages.RfrVisitsSearch}?type=${EnumVisitMode[props.type]}&menu=${
          visitClosed || (visitReopened && authorizedEirRole.value) ? "VisitsAsRc" : props.menuUserRole
        }`;
      },
      "VisitInspectionForm.vue",
      "addCommentsAsync"
    );
  } finally {
    formSubmitting.value = false;
  }
};

const saveFormAsync = async function (autosave: boolean = false) {
  if (!saveAllowed.value || savingForm.value || formSubmitting.value || formSubmitted.value) return;

  savingForm.value = true;

  try {
    const saveModel = await createSaveModelAsync();
    const files = saveModel.Files;
    saveModel.Files = [];

    const uploadFilesModel = {
      VisitId: Number(props.visitId) ?? 0,
      Files: files,
    } as UploadVisitInspectionFilesRequest;

    const showSaveError = (message: string) => {
      notificationStore.notify({
        message: message,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    };

    console.log("VisitInspectionForm, saving", formData.value);

    let savePromise = Promise.resolve();
    let uploadPromise = Promise.resolve();

    // Initiate form save only if form data has changed
    if (!savedFormState || JSON.stringify(savedFormState) != JSON.stringify(saveModel)) {
      savePromise = handleResponsePromiseAsync(
        api.post("SaveEirFormData", saveModel as EirFormData),
        (data) => {
          if (!data && !autosave) {
            showSaveError("Error saving form data");
            return;
          }
          lastSavedDate.value = new Date();
          notificationStore.notify({
            message: autosave ? "Form data was automatically saved" : "Form saved successfully",
            type: NotificationTypeEnum.success,
            duration: 5000,
          });
          savedFormState = saveModel as EirFormData;
        },
        "VisitInspectionForm.vue",
        "saveFormAsync",
        () => showSaveError("Error saving form data")
      );
    } else if (!autosave && files.length == 0) {
      notificationStore.notify({
        message: "No changes to save",
        type: NotificationTypeEnum.info,
        duration: 5000,
      });
    }

    if (files.length > 0) {
      uploadPromise = handleResponsePromiseAsync(
        api.post("SaveVisitInspectionFiles", uploadFilesModel),
        (data) => {
          if (!data && !autosave) {
            showSaveError("Error saving files");
            return;
          }
          lastSavedDate.value = new Date();
          notificationStore.notify({
            message: autosave ? "Files were automatically saved" : "Files saved successfully",
            type: NotificationTypeEnum.success,
            duration: 5000,
          });
          // Reset the uploaded files view
          formData.value.uploadedDocs = [];
          uploadKey.value++;
        },
        "VisitInspectionForm.vue",
        "saveFormAsync",
        () => showSaveError("Error saving files")
      );
    }

    await Promise.all([savePromise, uploadPromise]);
  } finally {
    savingForm.value = false;
  }
};

const onSaveClicked = function () {
  saveFormAsync();
};

const onApplyNAToAll = function (value: boolean) {
  // Apply N/A to all eligible questions in all sections
  const allAnswers = [
    ...sortedSections.value.flatMap((s) => s.answers ?? []),
    ...generalCustomQuestions.value,
    ...ingredientsCustomQuestions.value,
    ...productsCustomQuestions.value,
  ];

  allAnswers.forEach((q) => {
    if (
      (q.VisitQuestionType === EnumVisitQuestionType.RadioButton ||
        q.VisitQuestionType === EnumVisitQuestionType.Checkboxes) &&
      (q.Options?.length == 0 || q.Options?.includes("N/A")) &&
      (value || q.AnswerValue === "N/A")
    ) {
      q.AnswerValue = value ? "N/A" : null;
    }
  });

  // Apply N/A to special tasks section
  if (showSpecialSection.value) {
    formData.value.hideSpecialTasks = true;
  }
};

const onClearAllAnswers = function () {
  if (!isInitialFillingOutOfForm.value) return;

  const allAnswers = [
    ...sortedSections.value.flatMap((s) => s.answers ?? []),
    ...generalCustomQuestions.value,
    ...ingredientsCustomQuestions.value,
    ...productsCustomQuestions.value,
  ];

  allAnswers.forEach((q) => {
    q.AnswerValue = null;
  });

  // Reset static question fields in General Questions section
  formData.value.virtualVisit = null;
  formData.value.isUrgent = null;
  formData.value.urgentMatters = null;
  formData.value.isUrgentComment = null;
  formData.value.generalComments = null;
  formData.value.confidentialComment = null;
  formData.value.timeSpent = null;
  formData.value.sendEmailCopy = false;
  formData.value.emailRecipients = null;
  formData.value.jewishOwned = null;

  // Reset Ingredients / Products static questions
  formData.value.incorrectIngredientsFound = null;
  formData.value.incorrectIngredientComments = null;
  formData.value.incorrectProductsFound = null;
  formData.value.incorrectProductComments = null;

  // Reset special tasks section
  if (showSpecialSection.value) formData.value.hideSpecialTasks = false;
};

const onCloseClicked = function () {
  // Validate that a comment is added for urgent EIRs
  if (requireUrgentComment.value && !formData.value.isUrgentComment) {
    notificationStore.notify({
      message: "Please add a comment to Question 4 detailing the steps you have taken to resolve the URGENT matter(s).",
      type: NotificationTypeEnum.error,
      duration: 8000,
    });
    return;
  }

  // Validate that a comment is added for reopen EIRs
  if (visitReport.value?.EIRStatus.toLowerCase().includes("reopen") && !formData.value.reopenComment) {
    notificationStore.notify({
      message: "Please add a comment to explain why you are closing this reopened visit in the Reopen Comment field.",
      type: NotificationTypeEnum.error,
      duration: 8000,
    });
    return;
  }

  addCommentsAsync(true);
};

const onReopenClicked = function () {
  // Validate that a comment is added for reopen EIRs
  if (!formData.value.reopenComment) {
    notificationStore.notify({
      message: "Please add a comment to explain why you are reopening this visit in the Reopen Comment field.",
      type: NotificationTypeEnum.error,
      duration: 8000,
    });
    return;
  }
  addCommentsAsync(false, true);
};

const populateVisitFormHeader = function () {
  const visitData = otherCompaniesData.value[0];
  if (!visitData) return;

  inspectionStore.visitType = visitData.VisitType;
  formData.value.visitType = visitTypes.find((t) => t.value == visitData.VisitTypeEnum) ?? visitTypes[0];

  formData.value.plantName = visitData.Plant;
  formData.value.plantContact = visitData.PlantContact;
  formData.value.plantContactEmail = visitData.Email;
  formData.value.plantContactPhone = visitData.Phone;
  formData.value.rcName = visitData.Rc;
  formData.value.rfrName = visitData.Rfr;
  formData.value.plantAddress = visitData.PlantAddress;
  formData.value.plantCity = visitData.PlantCity;
  formData.value.plantState = visitData.PlantState;
  formData.value.plantCountry = visitData.PlantCountry;
  formData.value.sentByRcName = visitData.SenderRc;
  formData.value.sentByRcEmail = visitData.SenderEmail;
  formData.value.groupLeader = visitData.GroupLeader;
  formData.value.visitDateRange = visitData.DateRange;
  inspectionStore.companyJewishOwned = visitData.JewishOwned ?? null;
};

const convertQuestionsDataToAnswers = function (
  questionsData: VisitQuestionDisplay[] | undefined
): VisitInspectionAnswerDisplay[] {
  if (!questionsData) return [];

  return questionsData
    .filter((q) => q.Answer !== null)
    .map((q) => {
      const a = q.Answer;
      return {
        QuestionId: q.Id,
        QuestionText: q.QuestionText,
        VisitQuestionType: q.VisitQuestionType,
        IsRequired: q.IsRequired,
        AnswerValue: a.AnswerText,
        DisplayOrder: q.DisplayOrder,
        IsAnnual: q.IsAnnual,
        SectionName: q.Section.SectionName,
        SectionDisplayOrder: q.Section.DisplayOrder,
        HasComment: q.HasComment,
        Options: q.VisitOptions,
        Comments: a.Comments,
        Rules: q.Rules,
      } as VisitInspectionAnswerDisplay;
    });
};

const populateVisitForm = function () {
  const visitData = visitReport.value;
  if (!visitData) return;

  inspectionStore.visitStatus = visitData.EIRStatus;
  inspectionStore.isUrgentStatus = visitData.UrgentActionReqAnswer ?? false;
  inspectionStore.lastCommentDate = new Date(visitData.LastCommentDate);
  formData.value.visitDate = new Date(visitData.VisitDate);

  formData.value.incorrectIngredientsFound = visitData.SchedAAnswer ?? false;
  formData.value.incorrectProductsFound = visitData.SchedBAnswer ?? false;
  formData.value.savedUrgentMatters = visitData.UrgentRfrComments;
  formData.value.isUrgent = inspectionStore.isUrgentStatus;
  formData.value.savedIsUrgentComment = visitData.UrgentComments;
  formData.value.savedGeneralComments = visitData.GeneralComments;
  formData.value.savedConfidentialComment = visitData.ConfidentialComments;
  formData.value.incorrectIngredientSavedComments = visitData.SchedAComments;
  formData.value.incorrectProductSavedComments = visitData.SchedBComments;
  formData.value.savedUrgentMatters = visitData.UrgentRfrComments;
  formData.value.savedIsUrgentComment = visitData.UrgentComments;
  formData.value.sendEmailCopy = visitData.SendToPlant == "Yes";
  formData.value.reopenSavedComment = visitData.ReopenedComments;
  formData.value.virtualVisit = visitData.IsVirtualVisit == "1";
  formData.value.timeSpent = visitData.TimeIn;
  formData.value.jewishOwned = visitData.JewishOwned;
  formData.value.answers = convertQuestionsDataToAnswers(visitData.QuestionsAndAnswers) ?? [];

  formData.value.incorrectIngredients = visitData.ScheduleAItems;
  formData.value.incorrectProducts = visitData.ScheduleBItems;

  const guide = plantGuides.value.find((g) => g.FormattedName == visitData.NameOfPlantEscort);
  if (guide) formData.value.plantGuide = guide;

  formData.value.specialTaskQuestions.forEach((question) => {
    question.answer = ((visitData as any)[question.propertyName + "Answer"] ?? null) as boolean | null;

    question.haveIssues = (visitData as any)[question.propertyName + "Issues"] as boolean;

    question.savedComments = (visitData as any)[question.propertyName + "Comments"] as EirFormComment[];
  });
};

const populateVisitDate = function () {
  const visitData = otherCompaniesData.value[0];

  if (visitData && new Date(visitData.DateVisited).getFullYear() > 1 && isInitialFillingOutOfForm.value) {
    formData.value.visitDate = new Date(visitData.DateVisited);
  } else {
    disableVisitDate.value = false;
  }
};

const populateVisitFormUsingSavedData = function (data: EirFormData) {
  const savedVisitDate = moment(data.VisitDate, "MM/DD/yyyy");
  formData.value.visitDate = savedVisitDate.isValid() ? savedVisitDate.toDate() : undefined;
  formData.value.incorrectIngredientsFound = data.SchedA == "Yes";
  formData.value.incorrectIngredientComments = data.SchedAComments;
  formData.value.incorrectProductsFound = data.SchedB == "Yes";
  formData.value.incorrectProductComments = data.SchedBComments;
  formData.value.urgentMatters = data.UrgentRfrComments;
  formData.value.isUrgent = data.UrgentActionReq == "Yes";
  formData.value.isUrgentComment = data.UrgentComments;
  formData.value.generalComments = data.GeneralComments;
  formData.value.confidentialComment = data.ConfidentialComment;
  formData.value.sendEmailCopy = data.SendToPlant == "Yes";
  formData.value.emailRecipients = data.PlantEmail;
  formData.value.guideFirstName = data.ContactFirstName;
  formData.value.guideLastName = data.ContactLastName;
  formData.value.guideEmail = data.ContactEmail;
  formData.value.guidePhone = data.ContactPhone;
  formData.value.virtualVisit = data.VirtualVisit == "Y";
  formData.value.timeSpent = data.TimeIn;
  formData.value.jewishOwned = data.JewishOwned;
  formData.value.answers = data.Answers ?? [];

  const guide = plantGuides.value.find((g) => g.FormattedName == data.NameOfPlantEscort);
  if (guide) formData.value.plantGuide = guide;

  formData.value.specialTaskQuestions.forEach((question) => {
    const answer = (data as any)[question.propertyName];
    // N/A - null, N - false, Iss / NoIss - true
    question.answer = answer == "" ? undefined : answer == "NA" ? null : answer == "N" || answer == "No" ? false : true;
    question.haveIssues = answer == "Iss";
    question.comments = (data as any)[question.propertyName + "Comment"];
  });

  formData.value.incorrectIngredients = data.SchedAGrid;
  formData.value.incorrectProducts = data.SchedBGrid;
};

const getVisitOtherCompaniesAsync = async function () {
  const otherCompaniesPromise = api.get("vCartVisitsOtherCompanies", {
    userName: main.userLogin,
    visitId: Number(props.visitId),
  });

  await handleResponsePromiseAsync(
    otherCompaniesPromise,
    (data) => {
      otherCompaniesData.value = data;
      populateVisitFormHeader();
    },
    "VisitInspectionForm.vue",
    "getVisitOtherCompaniesAsync"
  );
};

const getVisitReportAsync = async function () {
  const visitReportPromise = api.get("GetEirDisplayReport", {
    visitId: Number(props.visitId),
  });

  await handleResponsePromiseAsync(
    visitReportPromise,
    (data) => {
      visitReport.value = data;
      populateVisitForm();
      populateVisitDate();
    },
    "VisitInspectionForm.vue",
    "getVisitReportAsync"
  );
};

const getRequirementsAsync = async function () {
  const requirementsPromise = api.get("GetVisitInspectionAdditionalRequirements", {
    visitId: Number(props.visitId),
  });

  await handleResponsePromiseAsync(
    requirementsPromise,
    (data) => {
      if (!data) return;
      requirements.value = data;
    },
    "VisitInspectionForm.vue",
    "getRequirementsAsync"
  );
};

const populateInitialFormDataAnswers = function () {
  if (!isInitialFillingOutOfForm.value || visitReport.value || !requirements.value) return;

  requirements.value.Questions.forEach((question) => {
    let answer = formData.value.answers.find((a) => a.QuestionId == question.Id);

    // Populate previous response question fields
    question.PreviousResponses.forEach((a) => {
      a.Options = question.VisitOptions;
      a.AnswerValue = a.AnswerText;
      a.QuestionText = question.QuestionText;
      a.VisitQuestionType = question.VisitQuestionType;
      a.IsAnnual = true;
    });

    // Populate previous responses for static General Questions (Jewish Owned, Time In/Out)
    if (
      question.Section.SectionName == "General Questions" &&
      !question.IsDynamicQuestion &&
      question.PreviousResponses.length > 0
    ) {
      generalCustomPreviousResponses.value.push(...question.PreviousResponses);
      return;
    }

    // If answer already exists, update it
    // (e.g. when requirements were changed and answers were saved before)
    if (answer) {
      answer.QuestionText = question.QuestionText;
      answer.DisplayOrder = question.DisplayOrder;
      answer.IsRequired = question.IsRequired;
      answer.IsAnnual = question.IsAnnual;
      answer.SectionName = question.Section.SectionName;
      answer.SectionDisplayOrder = question.Section.DisplayOrder;
      answer.VisitQuestionType = question.VisitQuestionType;
      answer.HasComment = question.HasComment;
      answer.Options = question.VisitOptions;
      answer.PreviousResponses = question.PreviousResponses;
      answer.Rules = question.Rules;
      return;
    }

    answer = {
      QuestionId: question.Id,
      QuestionText: question.QuestionText,
      DisplayOrder: question.DisplayOrder,
      IsRequired: question.IsRequired,
      IsAnnual: question.IsAnnual,
      SectionName: question.Section.SectionName,
      SectionDisplayOrder: question.Section.DisplayOrder,
      VisitQuestionType: question.VisitQuestionType,
      HasComment: question.HasComment,
      Options: question.VisitOptions,
      PreviousResponses: question.PreviousResponses,
      Rules: question.Rules,
    } as VisitInspectionAnswerDisplay;
    formData.value.answers.push(answer);
  });

  // Delete answers that are not in the requirements
  // (e.g. when answers were saved before requirements were changed)
  formData.value.answers = formData.value.answers.filter((a) => {
    const question = requirements.value!.Questions.find((q) => q.Id == a.QuestionId);
    if (!question || !a.SectionName) return false;
    // Static General Questions (Jewish Owned, Time In/Out) are handled separately, not as custom questions
    return a.SectionName != "General Questions" || question.IsDynamicQuestion;
  });

  // Don't show annual questions by default if they were answered previously
  if (hideAnnualQuestionsAllowed.value) showAnnualQuestions.value = false;
};

const populateFormSections = function () {
  formData.value.answers.forEach((answer) => {
    if (answer.SectionName == "General Questions") {
      generalCustomQuestions.value.push(answer);
      return;
    }

    if (answer.SectionName == "Ingredients") {
      ingredientsCustomQuestions.value.push(answer);
      return;
    }

    if (answer.SectionName == "Products") {
      productsCustomQuestions.value.push(answer);
      return;
    }

    const answerSection = sortedSections.value.find((s) => s.sectionName == answer.SectionName);
    if (!answerSection) {
      sortedSections.value.push({
        answers: [answer],
        sectionName: answer.SectionName,
        sectionOrder: answer.SectionDisplayOrder,
      });
    } else {
      answerSection.answers.push(answer);
    }
  });

  // Sort sections by their order
  sortedSections.value.sort((a, b) => a.sectionOrder - b.sectionOrder);
};

const getSavedDataAsync = async function () {
  const visitReportsPromise = api.get("GetSavedEirFormData", {
    visitId: Number(props.visitId),
  });

  await handleResponsePromiseAsync(
    visitReportsPromise,
    async (data) => {
      if (!data) return;
      populateVisitFormUsingSavedData(data);

      // Save the form state to compare with later
      const saveModel = await createSaveModelAsync();
      saveModel.Files = [];
      savedFormState = saveModel;
    },
    "VisitInspectionForm.vue",
    "getSavedDataAsync"
  );
};

const getPlantGuidesAsync = async function (plantId: number) {
  // #23141: no existing plant contacts for unscheduled visits with no plant
  if (isInitialFillingOutOfForm.value && otherCompaniesData.value[0]?.PlantId == 0) {
    plantGuides.value = [plantGuideNewContact];
    formData.value.plantGuide = plantGuides.value[0];
    return;
  }

  const plantGuidesPromise = api.get("GetFacilityContactsInPlant", {
    companyId: otherCompaniesData.value[0]?.CompanyId ?? 0,
    plantId: plantId,
    visitId: Number(props.visitId),
  });

  return handleResponsePromiseAsync(
    plantGuidesPromise,
    (data) => {
      plantGuides.value = [plantGuideDefault, ...data, plantGuideNewContact];
      formData.value.plantGuide = plantGuides.value[0];
    },
    "VisitInspectionForm.vue",
    "getPlantGuides"
  );
};

const getRfrTeamMembersAsync = async function () {
  const rfrTeamMembersPromise = api.get("GetRfrTeamMembers", {
    visitId: Number(props.visitId),
  });

  await handleResponsePromiseAsync(
    rfrTeamMembersPromise,
    (data) => {
      teamMembers.value = [teamMemberDefault, ...data];
    },
    "VisitInspectionForm.vue",
    "getRfrTeamMembersAsync"
  );
};

const confirmSubmission = function () {
  showConfirmationModal.value = false;
  submissionConfirmed.value = true;
  onSubmit();
};

const closeResultsModal = function () {
  const isRc = main.isInRole(UserRoles.ROLE_RABBINIC_COORDINATOR) || main.isInRole(UserRoles.ROLE_GROUP_LEADER);
  window.location.href = `${Pages.RfrVisitsSearch}?type=Inspection&menu=VisitsAsRfr`;
};

const downloadInitialVisitTemplate = function () {
  api.getDownload("GetDocFile", {
    fileName: "InitialInspectionReportQuestionnaire",
  });
};

const calculateEndSectionNumber = function (sectionIndex: number) {
  // Visit Info + General Questions are always present
  let sectionNumber = 2;
  // Add non special tasks section if visible (Ingredients/Products)
  if (showNonSpecialSection.value) sectionNumber += 2;
  // Add special tasks section if visible
  if (showSpecialSection.value) sectionNumber++;
  return sectionNumber + sortedSections.value.length + sectionIndex + 1;
};

// Auto-save form data every minute
let autoSaveIntervalId: ReturnType<typeof setInterval> | null = null;

const setAutoSaveInterval = function () {
  autoSaveIntervalId = setInterval(() => {
    saveFormAsync(true);
  }, 1000 * 60);
};

setAutoSaveInterval();

watchEffect(() => {
  inspectionStore.isGroupVisit = isGroupVisit.value;
});

watch(
  () => formData.value.hideSpecialTasks,
  () => {
    if (formData.value.hideSpecialTasks) {
      formData.value.specialTaskQuestions.forEach((question) => {
        question.answer = null;
      });
    }
  }
);

onBeforeMount(async () => {
  let promises = [] as Promise<any>[];
  await getVisitOtherCompaniesAsync();
  promises.push(getPlantGuidesAsync(otherCompaniesData.value[0]?.PlantId ?? 0));
  // Await for visit reports as isInitialFillingOutOfForm depends on it
  promises.push(getVisitReportAsync());
  await Promise.all(promises);
  promises = [];
  if (isInitialFillingOutOfForm.value) {
    promises.push(getRfrTeamMembersAsync());
    promises.push(getRequirementsAsync());
  }
  if (saveAllowed.value) promises.push(getSavedDataAsync());
  await Promise.all(promises);
  populateInitialFormDataAnswers();
  populateFormSections();
  inspectionStore.formLoaded = true;
});

onUnmounted(() => {
  if (autoSaveIntervalId !== null) {
    clearInterval(autoSaveIntervalId);
    autoSaveIntervalId = null;
  }
});
</script>

<style lang="scss">
.visit-inspection-form {
  .plant-guide-dropdown,
  .team-list-dropdown {
    max-width: 300px;
  }
}
</style>

<style scoped lang="scss">
.visit-inspection-form-wrapper {
  display: block;

  // Desktop: horizontal layout with sidebar on left
  @include media-breakpoint-up(lg) {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    width: 100%;
  }
}

.visit-inspection-form {
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;

  @include media-breakpoint-up(lg) {
    flex: 1;
    min-width: 0;
    max-width: 900px;
  }

  .error-message {
    font-weight: bold;
    color: red;
  }

  .plant-dropdown {
    @include media-breakpoint-up(sm) {
      width: 240px;
    }
  }

  .active-section {
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: -8px;
      top: 0;
      bottom: 0;
      width: 4px;
      background-color: #0056b3;
    }
  }

  &__annual-questions-toggle {
    margin-bottom: 24px;

    @media print {
      display: none;
    }
  }

  &__visit-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 5px;
    width: 100%;
    align-items: center;

    &.company-row {
      align-items: flex-end;
      justify-content: space-between;
      flex-flow: wrap-reverse;
    }
  }

  &__save-wrapper {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 200px;
  }

  &__visit-cell {
    flex: 1;
  }

  &__visit-control {
    display: flex;
    margin-bottom: 20px;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    &.column {
      flex-direction: column;
    }

    &.grow {
      flex: 1;
    }

    & > label {
      text-wrap: nowrap;
    }

    & > .k-dropdownlist {
      min-width: 120px;
    }
  }

  .visit-date-picker {
    max-width: 200px;
  }

  &__plant-guide-new {
    background: #efefef;
    padding: 12px;
    border-radius: 8px;
  }

  .visit-header {
    background: #efefef;
    padding: 15px;
    font-size: 14px;
    margin-bottom: 20px;
    font-weight: bold;
    border-radius: 8px;
    display: flex;
    gap: 8px;
  }

  &__question {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
  }

  &__question-checkbox {
    margin-bottom: 12px;
    display: block;
  }

  &__non-special,
  &__additional-questions {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__email-recipients {
    width: 100%;
    margin-bottom: 12px;
  }

  &__actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  &__submit {
    max-width: 200px;
  }

  &__text {
    font-weight: bold;
  }

  &__reopen-button,
  &__close-button {
    margin-top: 18px;
    max-width: 200px;
  }

  .mb-0 {
    margin-bottom: 0;
  }

  &__previous {
    max-width: 200px;
  }

  .incorrect-item-cards-view {
    margin-top: 12px;
  }

  textarea {
    resize: vertical;
    min-height: unset;
    padding: 12px 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.3s ease;
    background-color: #ffffff;

    &:hover {
      border-color: #bdbdbd;
    }

    &:focus {
      outline: none;
      border-color: #0056b3;
      background-color: #f5f9ff;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
    }
  }

  .invalid {
    color: red;
    font-size: 18px;
    font-weight: bold;
  }

  &__radio-wrapper {
    display: flex;
    gap: 16px;
  }

  input[type="time"] {
    padding: 10px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    color: #495057;
    transition: all 0.2s ease;

    &:hover {
      border-color: #adb5bd;
    }

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      outline: 0;
    }

    &:disabled {
      background-color: #e9ecef;
      opacity: 1;
    }
  }

  &__comment-instruction {
    margin-top: 16px;
  }

  &__header-container {
    background: #244880;
    padding: 24px;
    border-radius: 8px 8px 0 0;

    box-shadow:
      0 4px 6px rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.06);

    @media print {
      color: black;
    }
  }

  &__header {
    font-size: 20px;
    font-weight: bold;
    margin: 0 0 16px 0;
    color: #ffffff;

    @media print {
      color: black;
    }

    &-title {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    &-subtitle-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 12px;
      margin-top: 4px;
    }

    &-subtitle {
      display: block;
      font-size: 16px;
      font-weight: bold;
      margin-top: 0;
      color: #dbeafe;  
      
      @media print {
          color: black;
      }
    }

    &-plant-info {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding-top: 12px;
      border-top: 1px solid rgba(255, 255, 255, 0.2);    
    }

    &-plant-name {
      font-size: 18px;
      font-weight: 600;
      color: #ffffff;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      .plant-type {
        font-size: 16px;
        font-weight: 600;
        color: #dbeafe;

        @media print {
          color: black;
        }
      }

      .plant-name-link {
        color: #ffffff;
        text-decoration: underline;
        cursor: pointer;

        &:hover {
          color: #dbeafe;
        }

        @media print {
          color: black;
        }
      }
    }

    &-plant-category {
      font-size: 13px;
      font-weight: 500;
      color: #dbeafe;
    }
  }

  .plant-status {
    font-weight: bold;
  }

  &__content {
    padding: 4px;
    padding-top: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    @include media-breakpoint-up(lg) {
      padding: 12px;
      padding-top: 0;
    }
  }

  .metadata-separator {
    color: rgba(219, 234, 254, 0.6);
    font-weight: 400;
    padding: 0 4px;
  }

  .metadata {
    color: #dbeafe;
    font-weight: 500;
    font-size: 14px;

    @media print {
          color: black;
   }
  }

  &__mobile-submit {
    position: fixed;
    bottom: 68px; // stacked above sidebar toggle (20px + 40px + 8px gap)
    right: 10px;
    width: 40px;
    height: 40px;
    min-width: unset;
    padding: 0;
    border-radius: 50%;
    background-color: #0056b3;
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    z-index: 1001;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
      background-color: #003d80;
    }

    &:disabled {
      opacity: 0.6;
    }

    @include media-breakpoint-up(lg) {
      display: none;
    }

    @media print {
      display: none;
    }
  }

  &__sample-template-button {
    align-items: center;
    padding: 8px 12px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;

    &:hover,
    &:focus {
      background: rgba(255, 255, 255, 0.2);
      color: #ffffff;
    }

    @media print {
      display: none;
    }
  }
}

.error-message__link {
  cursor: pointer;
  text-decoration: underline;
  color: red;
  font-weight: bold;

  &:hover {
    color: #b71c1c;
    text-decoration: none;
  }
}
</style>
